import { DIALOG_DATA, DialogRef } from '@angular/cdk/dialog';
import { Component, inject } from '@angular/core';
import { TranslatePipe } from '@ngx-translate/core';

export interface UserBlockDialogData {
  blocked: boolean;
  name: string;
  save: () => Promise<boolean>;
}

/** Reuses the Bootstrap modal markup and CDK focus management of the discard dialog. */
@Component({
  standalone: true,
  imports: [TranslatePipe],
  styles: `
    .block-dialog { background: var(--bs-body-bg); color: var(--bs-body-color); }
    .block-cancel {
      --bs-btn-color: var(--bs-body-color);
      --bs-btn-border-color: var(--bs-body-color);
      --bs-btn-hover-bg: var(--bs-body-color);
      --bs-btn-hover-color: var(--bs-body-bg);
      --bs-btn-hover-border-color: var(--bs-body-color);
      --bs-btn-active-bg: var(--bs-body-color);
      --bs-btn-active-color: var(--bs-body-bg);
      --bs-btn-active-border-color: var(--bs-body-color);
      --bs-btn-disabled-color: var(--bs-body-color);
      --bs-btn-disabled-border-color: var(--bs-body-color);
    }
    .btn { min-height: 44px; }
  `,
  template: `
    <div class="block-dialog modal-content border border-primary rounded-3 shadow">
      <div class="modal-header bg-primary text-black rounded-top-2 p-1">
        <h2 class="modal-title fs-5" id="user-block-title">
          {{
            (data.blocked ? 'USER_BLOCK_CONFIRM_TITLE' : 'USER_UNBLOCK_CONFIRM_TITLE') | translate
          }}
        </h2>
      </div>
      <div class="modal-body m-2" id="user-block-message">
        <p>
          {{
            (data.blocked ? 'USER_BLOCK_CONFIRM_MESSAGE' : 'USER_UNBLOCK_CONFIRM_MESSAGE')
              | translate
          }}
        </p>
        <p class="text-break">
          <strong>{{ data.name }}</strong>
        </p>
        @if (error) {
          <p class="text-danger" role="alert">
            {{ (data.blocked ? 'USER_BLOCK_FAILURE' : 'USER_UNBLOCK_FAILURE') | translate }}
          </p>
        }
      </div>
      <div class="modal-footer border-secondary gap-2 m-2" [attr.aria-busy]="saving">
        <button
          type="button"
          class="btn block-cancel"
          [disabled]="saving"
          (click)="dialog.close(false)"
        >
          {{ 'CANCEL' | translate }}
        </button>
        <button type="button" class="btn btn-primary text-black" [disabled]="saving" (click)="save()">
          @if (saving) {
            <span class="spinner-border spinner-border-sm" aria-hidden="true"></span>
          }
          {{ (data.blocked ? 'USER_BLOCK' : 'USER_UNBLOCK') | translate }}
        </button>
      </div>
    </div>
  `,
})
export class UserBlockDialogComponent {
  readonly data = inject<UserBlockDialogData>(DIALOG_DATA);
  readonly dialog = inject<DialogRef<boolean>>(DialogRef);
  saving = false;
  error = false;
  async save(): Promise<void> {
    if (this.saving) return;
    this.saving = true;
    this.error = false;
    this.dialog.disableClose = true;
    try {
      this.dialog.close(await this.data.save());
    } catch {
      this.error = true;
    } finally {
      this.saving = false;
      this.dialog.disableClose = false;
    }
  }
}
